import { GenericTextOperators } from '@mairistem/tools';
import dayjs from 'dayjs';
import _ from 'lodash';
import { toast } from 'react-toastify';
import { ETATS_FACTURE } from '@/constants';
import type { FactureImpayeeListeItemType, FactureType, RegieType } from '@/types';
import type { FactureListeItemType, FactureListePrelevementItemType } from '@/types/facture';
import { downloadDocumentFile } from './file';
import * as utils_string from './string';

/**
 * Détermine la couleur selon l'état de la facture
 * @param etat - État de la facture
 * @returns Couleur correspondant à l'état
 */
export const getColorFromEtat = (
  etat: string,
):
  | 'red'
  | 'yellow'
  | 'green'
  | 'blue'
  | 'orange'
  | 'teal'
  | 'purple'
  | 'pink'
  | 'brown'
  | 'grey' => {
  switch (etat) {
    case ETATS_FACTURE.IMPAYE_TOTAL:
      return 'red';
    case ETATS_FACTURE.IMPAYE_PARTIEL:
      return 'orange';
    case ETATS_FACTURE.ENCAISSEMENT_PARTIEL:
      return 'yellow';
    case ETATS_FACTURE.ENCAISSEE:
      return 'green';
    // biome-ignore lint/complexity/noUselessSwitchCase: case explicite conservé pour lisibilité métier
    case ETATS_FACTURE.A_ENCAISSER:
    default:
      return 'grey';
  }
};

export type FormatFactureToListeItemType = (
  facture: FactureType,
  regie?: RegieType,
) => FactureListeItemType;

// Formate les données pour l'affichage dans la liste
// avec seulement les data dont on a besoin
export const formatFactureToListeItem: FormatFactureToListeItemType = (facture, regie) => {
  const {
    identifiant,
    identifiantPayeur,
    numero,
    payeur: {
      adresse,
      commentaire: commentairePayeur,
      civilite,
      nom: nomPayeur,
      prenom: prenomPayeur,
    },
    nomFacturation,
    cycle,
    etat,
    lignes,
    recus,
    prelevee,
    dateRejet,
    identifiantDocument,
  } = facture;

  const { regieProlongee, joursEncaissementSupplementaires } = regie || {};

  const {
    titre: cycleFacturation,
    dateValidationPosteComptable,
    dateEmission,
    dateLimite,
    logicielFacturier: { code: codeLogicielFacturier },
    relances,
  } = cycle;

  const identite = `${civilite?.abreviation || ''} ${utils_string.toOfficialLastname(nomPayeur || '')} ${utils_string.toOfficialFirstname(prenomPayeur || '')}`;
  const cycleTitre = cycleFacturation || '';

  // Reste à payer sur cette facture
  const restePayer = _.sumBy(lignes, 'reste');
  // Montant total de la facture
  const montant = _.sumBy(lignes, 'montant');

  let adrFacturation = '';

  if (_.isNumber(adresse?.numeroVoirie) && adresse?.numeroVoirie !== 0) {
    adrFacturation += `${adresse?.numeroVoirie} `;
  }

  if (!_.isEmpty(adresse?.lettre)) {
    adrFacturation += `${adresse?.lettre} `;
  }

  if (!_.isEmpty(adresse?.complementNumeroVoirie)) {
    adrFacturation += `${adresse?.complementNumeroVoirie} `;
  }

  if (!_.isEmpty(adresse?.libelleVoie)) {
    adrFacturation += `${adresse?.libelleVoie}`;
  }

  if (!_.isEmpty(adresse?.numeroCodePostal)) {
    adrFacturation += ` ${adresse?.numeroCodePostal}`;
  }

  if (!_.isEmpty(adresse?.localite)) {
    adrFacturation += ` ${adresse?.localite}`;
  }

  if (!_.isEmpty(adresse?.libellePays)) {
    adrFacturation += ` ${adresse?.libellePays}`;
  }

  return {
    identifiant,
    numero,
    identifiantPayeur,
    nomPayeur: _.trim(identite),
    commentairePayeur,
    nomFacturation,
    cycleFacturation: _.trim(cycleTitre),
    dateValidationPosteComptable,
    source: codeLogicielFacturier,
    dateFacture: dateLimite,
    dateFactureProlongee: regieProlongee
      ? dayjs(dateLimite).add(joursEncaissementSupplementaires, 'day').format('YYYY-MM-DD')
      : null,
    dateEmission,
    adresseFacturation: adrFacturation,
    restePayer,
    montantFacture: montant,
    etat,
    recus,
    prelevee,
    dateRejet,
    dateDerniereRelance: _.last(relances)?.date,
    identifiantDocument,
  };
};

/**
 * Télécharge le PDF d'une facture intégrée par flux (déposé par le facturier sur son bucket S3)
 * @param identifiantDocument - Identifiant du document `facture` lié à la facture
 */
export const downloadFacturePdf = async (identifiantDocument: number) => {
  try {
    await downloadDocumentFile(identifiantDocument, 'facture.pdf');
  } catch {
    toast.error('Erreur lors du téléchargement de la facture PDF');
  }
};

export const formatFacturesToListeItem = (factures: FactureType[], regie: RegieType) =>
  _.map(factures, (facture) => formatFactureToListeItem(facture, regie));

export const getFacturesEncaissementListeDefaultFilters = () => [
  {
    // Utilisé par le useListeSearchSort
    operator: 'DANS_LISTE',
    textOperator: GenericTextOperators.DANS_LISTE,
    propertyName: 'etat',
    value: [ETATS_FACTURE.A_ENCAISSER, ETATS_FACTURE.ENCAISSEMENT_PARTIEL],
    // Ci-dessous, normalement pas utilisé mais obligatoire pour TS
    type: 'string',
    predicate: null,
  },
];

export const formatFacturesToPrelevementListeItem = (factures: FactureType[]) =>
  _.map(factures, formatFactureToPrelevementListeItem);

// Formate les données pour l'affichage dans la liste
// avec seulement les data dont on a besoin
export const formatFactureToPrelevementListeItem = (
  facture: FactureType,
): FactureListePrelevementItemType => {
  const {
    identifiant,
    numero,
    etat: etatFacture,
    cycle: {
      titre,
      datePrelevement,
      dateEmission,
      logicielFacturier: { code: codeLogicielFacturier },
    },
    lignes,
    titulaireComptePreleve,
    compteBancairePreleve,
    dateRejet,
    motifRejet,
  } = facture;

  // Montant total de la facture
  const montant = _.sumBy(lignes, 'montant');

  return {
    identifiant,
    numero,
    datePrelevement,
    dateEmission,
    montant,
    cycle: _.trim(titre || ''),
    etatFacture,
    source: codeLogicielFacturier,
    titulaireComptePreleve,
    compteBancairePreleve,
    dateRejet,
    motifRejet,
  };
};

export const formatFacturesToImpayeeListeItem = (factures: FactureType[], regie: RegieType) =>
  _.map(factures, (facture) => formatFactureToImpayeeListeItem(facture, regie));

// Formate les données pour l'affichage dans la liste
// avec seulement les data dont on a besoin
export const formatFactureToImpayeeListeItem = (
  facture: FactureType,
  regie: RegieType,
): FactureImpayeeListeItemType => {
  const {
    identifiant,
    numero,
    cycle: {
      dateLimite,
      dateEmission,
      logicielFacturier: { code: codeLogicielFacturier },
    },
    lignes,
    payeur: { civilite, nom: nomPayeur, prenom: prenomPayeur },
  } = facture;

  const { regieProlongee, joursEncaissementSupplementaires } = regie || {};

  // Montant total de la facture
  const montant = _.sumBy(lignes, 'montant');
  // Reste à payer de la facture
  const resteAPayer = _.sumBy(lignes, 'reste');

  const identite = `${civilite?.abreviation || ''} ${utils_string.toOfficialLastname(nomPayeur || '')} ${utils_string.toOfficialFirstname(prenomPayeur || '')}`;

  const dateLimiteFinale = regieProlongee
    ? dayjs(dateLimite).add(joursEncaissementSupplementaires, 'day').format('YYYY-MM-DD')
    : dateLimite;

  return {
    identifiant,
    numero,
    dateLimite: dateLimiteFinale,
    dateEmission,
    nomPayeur: _.trim(identite),
    montant,
    source: codeLogicielFacturier,
    resteAPayer,
  };
};
